import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import useIntersectionObserver from "../../../../../../../hooks/use-intersection-observer";
import useCombinedRefs from "../../../../../../../hooks/use-combined-refs";
import useSearch from "../../../../../../../hooks/use-search";
import {DataGridColumnAlignments, DataGridDensities, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import {Box, Typography} from "@mui/material";
import PartnerDataGrid from "../../../../../../../containers/data-grid";


const dataGridSaveConfig = (id) => ({
    name: `LookupAnalyticsDashboardView_${id}`,
    version: 2,
})

const dataGridColumnNames = {
    navigate: '__navigate__',
}

const LookupAnalyticsDashboardViewTable = ({
                                               identifier,
                                               filters,
                                               title,
                                               columnDefinitions,
                                               navigateOnClick,
                                               fetchData,
                                           }) => {
    const [data, setData] = useState([]);
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.analytics.lookup-dashboard'});
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const canExport = operatorAccessLevels.packages.dashboard.lookupAnalytics
        && operatorAccessLevels.packages.dashboard.exportLookupAnalytics;

    /**@type {React.MutableRefObject<HTMLElement>}*/
    const containerRef = useRef();
    const [intersectionObserverRef, isIntersecting] = useIntersectionObserver();
    const ref = useCombinedRefs(intersectionObserverRef, containerRef);

    /**
     * Searches over the tasks of the current user in the notebook.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const forApi = {
            period: {
                startDateTime: args.filters?.startDate,
                endDateTime: args.filters?.endDate,
            },
            industryId: args.filters?.industryId,
            customerId: args.filters.customer?.id,
            operatorId: args.filters.operator?.id,
            pageNumber: args.paginationInfo?.currentPage,
        }
        let data = await fetchData(identifier, forApi, args.cancelTokenSource.token);
        if (!isMounted())
            return;
        setData(data ?? []);
        args.setPaginationInfo((prevState) => ({
            ...prevState,
            length: (data?.length ?? 0) === 0
                ? prevState.currentPage > 0 ? 0 : prevState.pageSize * prevState.currentPage
                : prevState.pageSize * prevState.currentPage + 1,
        }));
    }, [fetchData, identifier, isMounted]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: {currentPage: 1, pageSize: 10},
        disabled: !isIntersecting,
        filters: {...filters},
    }), [isIntersecting, filters]);

    const searchData = useSearch(search, initialSearchDataSettings, false)
    const {setFilters} = searchData;

    /**
     * When the filters change, update the search data
     */
    useEffect(() => {
        if (!isIntersecting)
            return;
        setFilters({...filters})
    }, [setFilters, filters, isIntersecting]);

    const saveConfig = useMemo(() => dataGridSaveConfig(identifier), [identifier]);
    const canNavigateOnClick = useMemo(() => !!navigateOnClick && !!operatorAccessLevels.packages.lookup.nexusSearch, [navigateOnClick, operatorAccessLevels.packages.lookup.nexusSearch]);

    const columns = useMemo(() => {
        /** @type {DataGridColumn[]}*/
        const res = [
            {
                name: dataGridColumnNames.navigate,
                title: "",
                width: 0,
                pinned: true,
                resizable: false,
                reorderable: false,
                pinnedToggleable: false,
                visibilityToggleable: false,
                sortable: false,
            }
        ];
        const definitions = Object.entries(columnDefinitions);
        definitions.sort(([, a], [, b]) => a.orderIndex - b.orderIndex);
        for (const [name, definition] of definitions) {
            res.push({
                name: name,
                title: definition.title,
                width: ['keyword', 'partNumber', 'customerName'].includes(name) ? 200 : 80,
                alignment: DataGridColumnAlignments.left,
                resizable: false,
                reorderable: false,
                pinnedToggleable: false,
                visibilityToggleable: false,
                sortable: false,
            })
        }
        return res;
    }, [columnDefinitions])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        version: saveConfig.version,
        name: saveConfig.name,
    });

    const dataGridRows = useMemo(() => {
        /** @type {DataGridRow[]} */
        const res = [];
        for (const entry of data) {
            const row = {
                key: entry.id,
                data: entry,
                cells: {
                    [dataGridColumnNames.navigate]: canNavigateOnClick &&
                        <IconButton color={'secondary'} onClick={() => navigateOnClick(identifier, entry)}>
                            <OpenInNew className='icon normal'/>
                        </IconButton>,
                }
            };
            for (const [name, definition] of Object.entries(columnDefinitions)) {
                row.cells[name] = definition.render ? definition.render(entry) : entry[name];
            }
            res.push(row);
        }
        return res;
    }, [canNavigateOnClick, columnDefinitions, data, identifier, navigateOnClick])

    /**
     * Exports all rows for the current filters as CSV via a single forExport request.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const currentFilters = searchData.filters;
        const forApi = {
            period: {
                startDateTime: currentFilters?.startDate,
                endDateTime: currentFilters?.endDate,
            },
            industryId: currentFilters?.industryId,
            customerId: currentFilters?.customer?.id,
            operatorId: currentFilters?.operator?.id,
            forExport: true,
        };
        const entries = await fetchData(identifier, forApi, undefined);
        if (!isMounted() || !entries?.length) {
            return;
        }
        const sortedDefinitions = Object.entries(columnDefinitions)
            .sort(([, a], [, b]) => a.orderIndex - b.orderIndex);
        return {
            title: title ?? t('export'),
            body: entries.map((entry) => {
                const row = {};
                for (const [name, definition] of sortedDefinitions) {
                    row[definition.title] = entry[name] ?? '';
                }
                return row;
            }),
        };
    }, [columnDefinitions, fetchData, identifier, isMounted, searchData.filters, t, title]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, height: '100%'}} ref={ref}>
                <Typography variant="body1" fontWeight={600}>
                    {title ?? ""}
                </Typography>
                <PartnerDataGrid
                    {...saveConfig}
                    hideToolbar={!canExport}
                    hideDensityChanging
                    hideColumnFiltering
                    hideLayoutRefreshing
                    hideFooterActions
                    hidePageSize
                    classNames={{container: 'lookup-analytics-dashboard-table'}}
                    initialLayoutState={{columns: columns}}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    getExportInfo={canExport ? exportToCsv : undefined}
                    getPaginationText={(pagination) => "Page " + pagination.currentPage}
                    visibleRows={5}
                    state={{
                        pagination: searchData.paginationInfo,
                        sortBy: searchData.orderBy,
                        loading: {state: searchData.loading},
                        density: DataGridDensities.compact,
                    }}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </Box>
        </>
    );
}

export default LookupAnalyticsDashboardViewTable;
